import {
    Box,
    Button,
    Collapse,
    DatePicker,
    Flex,
    Form,
    Grid,
    Label,
    NumberField,
    Select,
    Text,
} from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { memo, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { statuses } from './TransactionsFilter.constants';
import { useAvailableCurrencies } from './TransactionsFilter.hooks';
import { messages } from './TransactionsFilter.messages';
import { schema } from './TransactionsFilter.schema';
import { FilterValues, TransactionsFilterProps } from './TransactionsFilter.types';

export const TransactionsFilter = memo<TransactionsFilterProps>((props) => {
    const { filter, onSubmit } = props;

    const form = useForm<FilterValues>({
        values: filter,
        resolver: zodResolver(schema),
    });

    const { currencies, isLoading: isLoadingCurrencies } = useAvailableCurrencies();

    const handleSubmit = form.handleSubmit((values) => {
        return onSubmit(values);
    });

    const handleReset = useCallback(() => {
        form.reset();
        onSubmit({});
    }, [form, onSubmit]);

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Collapse
                name={messages.title}
                color='white100'
                radius='small'
                actions={
                    <Grid gridTemplateColumns='max-content max-content' columnGap={8}>
                        <Button color='blue10' size='xsmall' onClick={handleReset}>
                            {messages.reset}
                        </Button>

                        <Button color='blue80' size='xsmall' type='submit'>
                            {messages.apply}
                        </Button>
                    </Grid>
                }
            >
                <Box spacing='0 24 16 24'>
                    <Flex alignItems='end' spacing='16' wrap>
                        <Box width={160}>
                            <Select
                                name='currencies'
                                label={messages.currency}
                                placeholder={messages.currencyPlaceholder}
                                items={currencies ?? []}
                                progress={isLoadingCurrencies}
                                multiple
                                size='small'
                                tagColor='blue80'
                            />
                        </Box>

                        <Box width={160}>
                            <Select
                                name='statuses'
                                label={messages.status}
                                placeholder={messages.statusPlaceholder}
                                items={statuses}
                                multiple
                                size='small'
                                tagColor='blue80'
                            />
                        </Box>

                        <div>
                            <Label color='midnight70'>{messages.date}</Label>

                            <Grid
                                alignItems='center'
                                gridTemplateColumns='max-content 120px max-content 120px'
                                columnGap={4}
                            >
                                <Text color='midnight70' font='label' fontSize='small'>
                                    {messages.from}
                                </Text>

                                <DatePicker name='createdAtGte' size='small' placeholder={messages.datePlaceholder} />

                                <Text color='midnight70' font='label' fontSize='small'>
                                    {messages.to}
                                </Text>

                                <DatePicker name='createdAtLte' size='small' placeholder={messages.datePlaceholder} />
                            </Grid>
                        </div>

                        <div>
                            <Label color='midnight70'>{messages.amount}</Label>

                            <Grid
                                alignItems='center'
                                gridTemplateColumns='max-content 120px max-content 120px'
                                columnGap={4}
                            >
                                <Text color='midnight70' font='label' fontSize='small'>
                                    {messages.from}
                                </Text>

                                <NumberField
                                    name='accountAmountGte'
                                    size='small'
                                    placeholder={messages.amountPlaceholder}
                                    allowFloat
                                />

                                <Text color='midnight70' font='label' fontSize='small'>
                                    {messages.to}
                                </Text>

                                <NumberField
                                    name='accountAmountLte'
                                    size='small'
                                    placeholder={messages.amountPlaceholder}
                                    type='number'
                                    allowFloat
                                />
                            </Grid>
                        </div>
                    </Flex>
                </Box>
            </Collapse>
        </Form>
    );
});

TransactionsFilter.displayName = 'TransactionsFilter';
